import { useEffect, useState } from "react"

import { getAptListAPI } from "@/apis/admin-api"
import { toast } from "sonner"

import SearchBar from "@/routes/admin/_components/search-bar"
import Pagination from "@/components/pagination"
import AptSelectTable from "./apt-select-table"

const AptSelectModal = ({ group, onClose, onSubmit }) => {
  const [keyword, setKeyword] = useState("")
  const [list, setList] = useState([])
  const [selectedCodes, setSelectedCodes] = useState(group?.codes ?? [])
  const [pagination, setPagination] = useState({
    page: 1,
    size: 5,
    total: 0
  })

  const searchApts = async (page = 1) => {
    try {
      const data = await getAptListAPI({
        keyword,
        page,
        size: pagination.size
      })

      setList(data.items ?? [])
      setPagination(data.pagination)
    } catch (err) {
      toast.error(err?.response?.data?.detail || "아파트 검색에 실패했습니다.")
    }
  }

  useEffect(() => {
    searchApts(1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const toggleApt = (code) => {
    setSelectedCodes((prev) => {
      if (prev.includes(code)) {
        return prev.filter((value) => value !== code)
      }

      return [...prev, code]
    })
  }

  const handleSearch = async () => {
    await searchApts(1)
  }

  const handlePageChange = async (page) => {
    await searchApts(page)
  }

  const handleSubmit = () => {
    onSubmit?.(selectedCodes)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-6">
      <div className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
        <header className="flex shrink-0 items-center justify-between border-b px-6 py-5">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">아파트 관리</h2>

            <p className="mt-1 text-sm text-gray-500">
              {group?.group_name} 그룹의 관리 대상을 선택합니다.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="
              flex h-8 w-8 items-center justify-center
              rounded-md text-xl text-gray-400
              transition-colors hover:bg-gray-100 hover:text-gray-700
            "
          >
            ×
          </button>
        </header>

        <main className="flex min-h-0 flex-1 flex-col px-6 py-5">
          <SearchBar
            placeholder="아파트 코드 또는 아파트 명으로 검색"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            onSubmit={handleSearch}
          />

          <div className="mt-5 mb-2 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-gray-800">관리 대상</span>

              <span className="text-sm text-gray-500">{selectedCodes.length}개 선택</span>
            </div>

            <span className="text-xs text-gray-400">
              총 {pagination.total.toLocaleString() ?? 0}개
            </span>
          </div>

          <AptSelectTable list={list} selectedCodes={selectedCodes} onToggle={toggleApt} />

          <Pagination
            currentPage={pagination.page}
            totalPages={Math.ceil(pagination.total / pagination.size)}
            onChange={handlePageChange}
            activeStyle="bg-toicos-navy"
          />
        </main>

        <footer className="flex shrink-0 items-center justify-between border-t px-6 py-4">
          <div className="text-sm text-gray-500">
            선택된 관리 대상{" "}
            <span className="font-semibold text-gray-800">{selectedCodes.length}개</span>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="
                rounded-md border border-gray-200
                px-5 py-2 text-sm
                text-gray-600
                transition-colors
                hover:bg-gray-50
              "
            >
              취소
            </button>

            <button
              type="button"
              onClick={handleSubmit}
              className="
                rounded-md bg-toicos-navy
                px-5 py-2 text-sm font-medium text-white
                transition-opacity
                hover:opacity-90
              "
            >
              적용
            </button>
          </div>
        </footer>
      </div>
    </div>
  )
}

export default AptSelectModal
